import { useEffect, useRef, useState } from "react";
import type { VideoConfig } from "./types";

interface CameraDimensions {
  width: number;
  height: number;
}

interface VideoPlayerProps {
  video: VideoConfig;
  dimensions?: CameraDimensions;
  margin?: number;
}

export function VideoPlayer({ video, dimensions, margin = 10 }: VideoPlayerProps) {
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const retryTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    // Clean up any pending retry timeout
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = null;
    }
    
    if (video.type === "dynamic") {
      fetchVideoUrl();
      // Set up polling every 10 minutes (600000 ms)
      const interval = setInterval(() => {
        fetchVideoUrl();
      }, 600000);

      return () => {
        clearInterval(interval);
        if (retryTimeoutRef.current) {
          clearTimeout(retryTimeoutRef.current);
        }
      };
    } else if (video.type === "image") {
      refreshImage();
      // Set up polling every 10 minutes (600000 ms) for live webcam images
      const interval = setInterval(() => {
        refreshImage();
      }, 600000);

      return () => {
        clearInterval(interval);
        if (retryTimeoutRef.current) {
          clearTimeout(retryTimeoutRef.current);
        }
      };
    } else {
      setVideoUrl(video.source);
      setLoading(false);
    }
  }, [video.type, video.source]);

  useEffect(() => {
    // Handle video URL changes
    if (!videoUrl || !videoRef.current) return;

    const isHLS = videoUrl.includes(".m3u8");

    if (isHLS) {
      // Check for native HLS support (Safari, Chrome on iOS)
      if (videoRef.current.canPlayType("application/vnd.apple.mpegurl")) {
        videoRef.current.src = videoUrl;
        videoRef.current.load();
        setLoading(false);
      } else {
        console.error("HLS not supported in this browser. Please use Safari or Chrome on iOS.");
        setError("HLS video playback not supported in this browser");
        setLoading(false);
      }
    } else {
      // Regular video file (mp4, webm, etc.)
      videoRef.current.src = videoUrl;
      videoRef.current.load();
      setLoading(false);
    }
  }, [videoUrl]);

  const fetchVideoUrl = async () => {
    if (video.type !== "dynamic") return;

    try {
      setLoading(true);
      setError(null);
      const response = await fetch(
        `/api/extract-video?url=${encodeURIComponent(video.source)}`
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to extract video URL");
      }

      const data = await response.json();
      setVideoUrl(data.videoUrl);
      setLoading(false);
    } catch (err) {
      console.error("Error fetching video URL:", err);
      setError(err instanceof Error ? err.message : "Failed to load video");
      setLoading(false);
      
      // Retry after 1 second
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
      retryTimeoutRef.current = setTimeout(() => {
        fetchVideoUrl();
      }, 1000);
    }
  };

  const refreshImage = () => {
    if (video.type !== "image") return;
    setError(null);
    // Add cache-busting query parameter to force refresh
    const cacheBuster = `?t=${Date.now()}`;
    setImageUrl(video.source + cacheBuster);
  };

  if (!video.visible) {
    return null;
  }

  // Calculate container styles based on dimensions
  const containerStyle: React.CSSProperties = dimensions
    ? {
        width: `${dimensions.width}px`,
        height: `${dimensions.height}px`,
        margin: `${margin}px`,
      }
    : {};

  if (loading && video.type === "dynamic") {
    return (
      <div
        ref={containerRef}
        className="video-container loading adaptive-video-item"
        data-video-id={video.id}
        style={containerStyle}
      >
        <div style={{ color: "#fff", padding: "2rem", textAlign: "center" }}>
          Loading video source...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        ref={containerRef}
        className="video-container error adaptive-video-item"
        data-video-id={video.id}
        style={containerStyle}
      >
        <div style={{ color: "#fff", padding: "2rem", textAlign: "center" }}>
          {error}
        </div>
      </div>
    );
  }

  if (!videoUrl && !imageUrl && video.type !== "youtube" && video.type !== "iframe" && video.type !== "image") {
    return null;
  }
  return (
    <div
      ref={containerRef}
      className={`video-container adaptive-video-item ${video.type === "youtube" ? "youtube-container" : ""}`}
      data-video-id={video.id}
      data-visible={video.visible.toString()}
      style={containerStyle}
    >
      <div className="video-title-overlay">{video.title}</div>
      {video.type === "youtube" && (
        <iframe
          className="youtube-iframe"
          src={`https://www.youtube.com/embed/${video.source}?autoplay=1&mute=1&loop=1&controls=0&playsinline=1&rel=0&modestbranding=1&iv_load_policy=3&cc_load_policy=0&disablekb=1&fs=0&showinfo=0&enablejsapi=1`}
          allow="autoplay; encrypted-media"
          allowFullScreen={false}
          loading="lazy"
          style={{ width: "100%", height: "100%", border: "none" }}
        />
      )}
      {(video.type === "video" || video.type === "dynamic") && videoUrl && (
        <video
          key={videoUrl}
          ref={videoRef}
          src={videoUrl}
          autoPlay
          muted
          loop
          playsInline
          style={{ width: "100%", height: "100%", objectFit: "cover" }}
          onLoadedData={() => {
            setLoading(false);
          }}
          onError={(e) => {
            console.error("Video error:", e);
            setError("Failed to load video");
            
            // Only retry for dynamic videos
            if (video.type === "dynamic") {
              // Retry after 1 second by refetching the URL
              if (retryTimeoutRef.current) {
                clearTimeout(retryTimeoutRef.current);
              }
              retryTimeoutRef.current = setTimeout(() => {
                fetchVideoUrl();
              }, 1000);
            }
          }}
        />
      )}
      {video.type === "iframe" && videoUrl && (
        <iframe
          src={videoUrl}
          allow="autoplay; encrypted-media"
          allowFullScreen
          loading="lazy"
          style={{ width: "100%", height: "100%", border: "none" }}
        />
      )}
      {video.type === "image" && imageUrl && (
        <img
          ref={imageRef}
          src={imageUrl}
          alt={video.title}
          style={{ width: "100%", height: "100%", objectFit: "contain" }}
          onError={(e) => {
            console.error("Image error:", e);
            setError("Failed to load image");
            setLoading(false);
          }}
        />
      )}
    </div>
  );
}

